import React, { useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Button, Form, InputGroup, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { SHOP_SIZES } from "../../../apollo/setting_apollo/query";
import { UPSERT_SHOP_SIZES } from "../../../apollo/setting_apollo/mutation";
import { GET_FABRICS } from "../../../apollo/fabric/query";
import { CREATE_FABRIC, DELETE_FABRIC, UPDATE_FABRIC } from "../../../apollo/fabric/mutation";
import { GET_PRESIGN_URL } from "../../../apollo/presignUrl/query";
import { JACKSPORT_SHOP_IDS, S3_URL } from "../../../const";
import { MSG_CREATE_SUCCESS, MSG_INCOMPLETE_DATA, MSG_INTERNAL_ERROR, MSG_LOADING, MSG_SHOP_UNAVAILABLE } from "../../../const/msg";
import EditFabricModal from "./EditFabricModal";
import { numberFormat } from "../../../helper";

function ShopSize() {
  const allowShopIds = JACKSPORT_SHOP_IDS;
  const localShopId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
    } catch (error) {
      return undefined;
    }
  }, []);
  const [sizeInput, setSizeInput] = useState("");
  const [sizes, setSizes] = useState([]);
  const [activeTab, setActiveTab] = useState("size");
  const [showFabricModal, setShowFabricModal] = useState(false);
  const [showEditFabricModal, setShowEditFabricModal] = useState(false);
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [editingFabric, setEditingFabric] = useState();
  const [fabricNameInput, setFabricNameInput] = useState("");
  const [fabricPriceInput, setFabricPriceInput] = useState("0");
  const [fabricOriginPriceInput, setFabricOriginPriceInput] = useState("0");
  const [fabricAmountRollInput, setFabricAmountRollInput] = useState("0");
  const [fabricQtyPerRollInput, setFabricQtyPerRollInput] = useState("0");
  const [fabricPricePerRollInput, setFabricPricePerRollInput] = useState("0");
  const [fabricOriginPricePerRollInput, setFabricOriginPricePerRollInput] = useState("0");
  const [fabricImageFile, setFabricImageFile] = useState();
  const [fabricImagePreview, setFabricImagePreview] = useState("");
  const getApolloErrorMessage = (error) => {
    const graphQLErrorMessage = error?.graphQLErrors?.[0]?.message;
    const networkErrorMessage = error?.networkError?.result?.errors?.[0]?.message || error?.networkError?.message;
    return graphQLErrorMessage || networkErrorMessage || error?.message || MSG_INTERNAL_ERROR;
  };

  const { loading: loadingShopSizes, refetch: refetchShopSizes } = useQuery(SHOP_SIZES, {
    variables: { shop: localShopId || "" },
    skip: !allowShopIds.includes(localShopId),
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setSizes((data?.shopSizes || []).map((item) => item?.value).filter(Boolean));
    },
    onError: (error) => {
      toast.error(getApolloErrorMessage(error));
    },
  });
  const [upsertShopSizes, { loading: saving }] = useMutation(UPSERT_SHOP_SIZES);
  const { data: fabricsData, loading: loadingFabrics, refetch: refetchFabrics } = useQuery(GET_FABRICS, {
    variables: {
      where: {
        shop: localShopId,
        isDeleted: false,
      },
    },
    skip: !allowShopIds.includes(localShopId),
    fetchPolicy: "cache-and-network",
    onError: (error) => {
      toast.error(getApolloErrorMessage(error));
    },
  });
  const [createFabric, { loading: creatingFabric }] = useMutation(CREATE_FABRIC);
  const [deleteFabric, { loading: deletingFabric }] = useMutation(DELETE_FABRIC);
  const [updateFabric, { loading: updatingFabric }] = useMutation(UPDATE_FABRIC);
  const [getPresignUrl] = useLazyQuery(GET_PRESIGN_URL);
  const fabrics = fabricsData?.fabrics?.data || [];
  const fabricTotal = fabricsData?.fabrics?.total ?? fabrics.length;
  const getFabricImageSrc = (fabric) => {
    const imageItem = fabric?.image?.[0];
    if (imageItem) {
      if (String(imageItem).startsWith("http") || String(imageItem).startsWith("data:")) {
        return imageItem;
      }
      return `${S3_URL}${imageItem}`;
    }
    if (fabric?.imageString) {
      return fabric.imageString;
    }
    return "/assets/image/packxi-tab.png";
  };
  const openEditFabricModal = (fabric) => {
    setEditingFabric({
      ...fabric,
      imageSrc: getFabricImageSrc(fabric),
    });
    setShowEditFabricModal(true);
  };
  const onSaveEditFabric = async (payload) => {
    if (!payload?.name) {
      return toast.warning(MSG_INCOMPLETE_DATA);
    }
    try {
      Swal.fire({
        title: "ກຳລັງບັນທຶກ...",
        text: "ກະລຸນາລໍຖ້າ",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      let filename = editingFabric?.image?.[0];
      if (payload?.imageFile) {
        const ext = payload.imageFile?.name?.split(".")?.pop() || "jpeg";
        filename = `${uuidv4()}.${ext}`;
        const presignResponse = await getPresignUrl({
          variables: {
            name: filename,
          },
        });
        const uploadUrl = presignResponse?.data?.preSignedUrl?.url;
        if (!uploadUrl) {
          throw new Error(MSG_INTERNAL_ERROR);
        }
        await axios.put(uploadUrl, payload.imageFile, {
          headers: {
            "Content-Type": "file/*; image/*",
            Accept: "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
            "Access-Control-Allow-Headers": "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
          },
        });
      }
      await updateFabric({
        variables: {
          where: {
            id: payload?.id,
          },
          data: {
            name: payload?.name,
            price: Number(payload?.price || 0),
            originPrice: Number(payload?.originPrice || 0),
            pricePerRoll: Number(payload?.pricePerRoll || 0),
            originPricePerRoll: Number(payload?.originPricePerRoll || 0),
            amountRoll: Number(payload?.amountRoll || 0),
            qtyPerRoll: Number(payload?.qtyPerRoll || 0),
            image: [filename],
          },
        },
      });
      await refetchFabrics();
      Swal.close();
      toast.success(MSG_CREATE_SUCCESS);
      setShowEditFabricModal(false);
      setEditingFabric(undefined);
    } catch (error) {
      Swal.close();
      toast.error(getApolloErrorMessage(error));
    }
  };
  const onDeleteFabric = async (fabricId) => {
    if (!fabricId) {
      return;
    }
    const result = await Swal.fire({
      title: "ຢືນຢັນການລົບ",
      text: "ທ່ານຕ້ອງການລົບຜ້າລາຍການນີ້ບໍ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ລົບ",
      cancelButtonText: "ຍົກເລີກ",
      confirmButtonColor: "#DC2626",
      cancelButtonColor: "#6B7280",
    });
    if (!result.isConfirmed) {
      return;
    }
    try {
      Swal.fire({
        title: "ກຳລັງລົບ...",
        text: "ກະລຸນາລໍຖ້າ",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      await deleteFabric({
        variables: {
          where: {
            id: fabricId,
          },
        },
      });
      await refetchFabrics();
      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "ລົບສຳເລັດ",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.close();
      toast.error(getApolloErrorMessage(error));
    }
  };

  const onAddSize = () => {
    const normalizedValue = sizeInput.trim().toUpperCase();
    if (!normalizedValue) {
      return;
    }
    if (sizes.includes(normalizedValue)) {
      setSizeInput("");
      return;
    }
    setSizes((prev) => [...prev, normalizedValue]);
    setSizeInput("");
  };

  const onDeleteSize = (size) => {
    setSizes((prev) => prev.filter((item) => item !== size));
  };

  const onSave = async () => {
    if (saving) {
      return toast.warning(MSG_LOADING);
    }
    if (!localShopId) {
      return toast.error(MSG_SHOP_UNAVAILABLE);
    }
    if (!sizes.length) {
      return toast.warning(MSG_INCOMPLETE_DATA);
    }
    try {
      const response = await upsertShopSizes({
        variables: {
          shop: localShopId,
          values: sizes,
        },
      });
      const valuesFromApi = (response?.data?.upsertShopSizes || []).map((item) => item?.value).filter(Boolean);
      setSizes(valuesFromApi);
      await refetchShopSizes();
      toast.success(MSG_CREATE_SUCCESS);
    } catch (error) {
      toast.error(getApolloErrorMessage(error));
    }
  };

  if (!allowShopIds.includes(localShopId)) {
    return (
      <div className="p-4">
        <h5>ຈັດການ Size / ຈັດການຜ້າ</h5>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h5>ຈັດການ Size / ຈັດການຜ້າ</h5>
      <div style={{ width: "100%", background: "#fff", padding: 16, borderRadius: 8 }}>
        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || "size")} className="mb-3">
          <Tab eventKey="size" title="ຈັດການ Size">
            <p>ສ້າງໄຊສ໌ຂອງຮ້าน (S/M/L/XL)</p>
            <InputGroup className="mb-3">
              <Form.Control
                placeholder="ປ້ອນຂະໜາດ"
                value={sizeInput}
                onChange={(e) => setSizeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onAddSize();
                  }
                }}
              />
              <Button variant="primary" onClick={onAddSize}>
                ເພີ່ມ
              </Button>
            </InputGroup>

            <Table bordered responsive>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>#</th>
                  <th>Size</th>
                  <th style={{ width: 120 }}>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {sizes.map((size, index) => (
                  <tr key={size}>
                    <td>{index + 1}</td>
                    <td>{size}</td>
                    <td>
                      <Button variant="outline-danger" size="sm" onClick={() => onDeleteSize(size)}>
                        ລົບ
                      </Button>
                    </td>
                  </tr>
                ))}
                {!sizes.length && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center" }}>
                      {loadingShopSizes ? <Spinner size="sm" /> : "-"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <Button variant="success" onClick={onSave} disabled={saving}>
              {saving ? <Spinner size="sm" /> : "ບັນທຶກ"}
            </Button>
          </Tab>
          <Tab eventKey="fabric" title="ຈັດການຜ້າ">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>ຈຳນວນລາຍການຜ້າ: {fabricTotal}</div>
              <Button variant="primary" onClick={() => setShowFabricModal(true)}>
                ເພີ່ມປະເພດຜ້າ
              </Button>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table bordered style={{ minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: "center" }}>ລຳດັບ</th>
                    <th style={{ width: 70 }}>ຮູບ</th>
                    <th>ຊື່ຜ້າ</th>
                    <th style={{ width: 130, textAlign: "right" }}>ຕົ້ນທຶນ/ກໍ້</th>
                    <th style={{ width: 130, textAlign: "right" }}>ລາຄາຂາຍ/ໂຕ</th>
                    <th style={{ width: 130, textAlign: "right" }}>ລາຄາຂາຍ/ກໍ້</th>
                    <th style={{ width: 100, textAlign: "center" }}>ຈຳນວນກໍ້</th>
                    <th style={{ width: 100, textAlign: "center" }}>ເສື້ອ/ກໍ້</th>
                    <th style={{ width: 100, textAlign: "center" }}>ລວມເສື້ອ</th>
                    <th style={{ width: 90, textAlign: "center" }}>ຈັດการ</th>
                  </tr>
                </thead>
                <tbody>
                  {fabrics.map((fabric, index) => (
                    <tr key={fabric?.id || index}>
                      <td style={{ textAlign: "center" }}>{index + 1}</td>
                      <td>
                        <img
                          src={getFabricImageSrc(fabric)}
                          alt={fabric?.name || "fabric"}
                          style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                          onClick={() => {
                            setPreviewImageUrl(getFabricImageSrc(fabric));
                            setShowImagePreviewModal(true);
                          }}
                        />
                      </td>
                      <td>{fabric?.name || "-"}</td>
                      <td style={{ textAlign: "right", fontWeight: "bold", color: "#1D4ED8" }}>
                        {numberFormat(fabric?.originPricePerRoll ?? 0)}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: "bold", color: "#DB2777" }}>
                        {numberFormat(fabric?.price ?? 0)}
                      </td>
                      <td style={{ textAlign: "right", color: "#6B7280", fontSize: "0.85rem" }}>
                        {numberFormat(fabric?.pricePerRoll ?? 0)}
                      </td>
                      <td style={{ textAlign: "center" }}>{fabric?.amountRoll ?? 0}</td>
                      <td style={{ textAlign: "center" }}>{fabric?.qtyPerRoll ?? 0}</td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>{fabric?.totalQty ?? 0}</td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10 }}>
                          <Button
                            variant="link"
                            style={{ padding: 0, color: "#1F3A60" }}
                            onClick={() => openEditFabricModal(fabric)}
                          >
                            <EditOutlinedIcon />
                          </Button>
                          <Button
                            variant="link"
                            style={{ padding: 0, color: "#DC2626" }}
                            disabled={deletingFabric}
                            onClick={() => onDeleteFabric(fabric?.id)}
                          >
                            <DeleteOutlineOutlinedIcon />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!fabrics.length && (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center" }}>
                        {loadingFabrics ? <Spinner size="sm" /> : "-"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>
        </Tabs>
      </div>

      <Modal show={showFabricModal} onHide={() => setShowFabricModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ເພີ່ມປະເພດຜ້າ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>ອັບໂຫລດຮູບຜ້າ</Form.Label>
            <Form.Control
              id="fabric-file-upload"
              type="file"
              accept="image/*"
              multiple={false}
              hidden
              onChange={async (e) => {
                if ((e.target.files?.length || 0) > 1) {
                  toast.warning("ເລືອກໄດ້ 1 ຮູບເທົ່ານັ້ນ");
                }
                const file = e.target.files?.[0];
                setFabricImageFile(file);
                setFabricImagePreview(file ? URL.createObjectURL(file) : "");
              }}
            />
            <label htmlFor="fabric-file-upload" style={{ margin: 0, width: "100%" }}>
              <div
                style={{
                  backgroundColor: "#F9FAFB",
                  height: 250,
                  width: "100%",
                  border: "1px dashed #93989F",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {fabricImagePreview ? (
                  <img src={fabricImagePreview} alt="fabric-preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                ) : (
                  <>
                    <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "#93989F" }} />
                    <p style={{ color: "#93989F", marginTop: 8, marginBottom: 4 }}>ເພີ່ມຮູບຜ້າ</p>
                    <p style={{ color: "#3C169B", textDecoration: "underline", marginBottom: 0 }}>Browse</p>
                  </>
                )}
              </div>
            </label>
            <p style={{ color: "#93989F", marginTop: 8, marginBottom: 0 }}>ກົດຮູບອີກເທື່ອ ເພື່ອປ່ຽນຮູບ</p>
          </Form.Group>
          <div className="mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Group>
              <Form.Label>ລາຄາຂາຍ (ຕໍ່ໂຕ)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                style={{ width: "100%", background: "#FDF2F8" }}
                value={fabricPriceInput}
                onChange={(e) => {
                  const price = parseFloat(e.target.value || 0);
                  setFabricPriceInput(e.target.value);
                  const qtyPerRoll = parseFloat(fabricQtyPerRollInput || 1);
                  if (qtyPerRoll > 0) setFabricPricePerRollInput(Math.ceil(price * qtyPerRoll).toString());
                }}
                placeholder="0"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>ລາຄາຕົ້ນທຶນ (ຕໍ່ກໍ້)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                style={{ width: "100%", background: "#F0F9FF" }}
                value={fabricOriginPricePerRollInput}
                onChange={(e) => {
                  const opRoll = parseFloat(e.target.value || 0);
                  setFabricOriginPricePerRollInput(e.target.value);
                  const qtyPerRoll = parseFloat(fabricQtyPerRollInput || 1);
                  if (qtyPerRoll > 0) setFabricOriginPriceInput(Math.ceil(opRoll / qtyPerRoll).toString());
                }}
                placeholder="0"
              />
            </Form.Group>
          </div>
          <div className="mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Group>
              <Form.Label>ຈຳນວນກໍ້ຜ້າ</Form.Label>
              <Form.Control
                type="number"
                min={0}
                style={{ width: "100%" }}
                value={fabricAmountRollInput}
                onChange={(e) => setFabricAmountRollInput(e.target.value)}
                placeholder="0"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>ຈຳນວນເສື້ອ,ໂສ້ງ/ຜ້າ1ກໍ້</Form.Label>
              <Form.Control
                type="number"
                min={0}
                style={{ width: "100%" }}
                value={fabricQtyPerRollInput}
                onChange={(e) => {
                  const qty = parseFloat(e.target.value || 0);
                  setFabricQtyPerRollInput(e.target.value);
                  if (qty > 0) {
                    setFabricPricePerRollInput(Math.ceil(parseFloat(fabricPriceInput || 0) * qty).toString());
                    setFabricOriginPriceInput(Math.ceil(parseFloat(fabricOriginPricePerRollInput || 0) / qty).toString());
                  }
                }}
                placeholder="0"
              />
            </Form.Group>
          </div>
          <p style={{ color: "#93989F", marginTop: 8, marginBottom: 0, fontSize: "0.85rem" }}>
            ໝາຍເຫດ: ຜ້າ1ກໍ້ ໄດ້ຈຳນວນເສື້ອຈັກຜືນ
          </p>
          <Form.Group className="mt-3">
            <Form.Label>ຊື່ປະເພດຜ້າ</Form.Label>
            <Form.Control value={fabricNameInput} onChange={(e) => setFabricNameInput(e.target.value)} placeholder="ປ້ອນຊື່ຜ້າ" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowFabricModal(false);
              setFabricNameInput("");
              setFabricPriceInput("0");
              setFabricOriginPriceInput("0");
              setFabricAmountRollInput("0");
              setFabricQtyPerRollInput("0");
              setFabricPricePerRollInput("0");
              setFabricOriginPricePerRollInput("0");
              setFabricImageFile(undefined);
              setFabricImagePreview("");
            }}
          >
            ຍົກເລີກ
          </Button>
          <Button
            variant="primary"
            disabled={creatingFabric}
            onClick={async () => {
              if (creatingFabric) {
                return toast.warning(MSG_LOADING);
              }
              if (!fabricNameInput.trim()) {
                return toast.warning(MSG_INCOMPLETE_DATA);
              }
              if (!localShopId) {
                return toast.error(MSG_SHOP_UNAVAILABLE);
              }
              if (!fabricImageFile) {
                return toast.warning(MSG_INCOMPLETE_DATA);
              }
              try {
                const ext = fabricImageFile?.name?.split(".")?.pop() || "jpeg";
                const filename = `${uuidv4()}.${ext}`;
                const presignResponse = await getPresignUrl({
                  variables: {
                    name: filename,
                  },
                });
                const uploadUrl = presignResponse?.data?.preSignedUrl?.url;
                if (!uploadUrl) {
                  return toast.error(MSG_INTERNAL_ERROR);
                }
                await axios.put(uploadUrl, fabricImageFile, {
                  headers: {
                    "Content-Type": "file/*; image/*",
                    Accept: "application/json",
                    "Access-Control-Allow-Origin": "*",
                    "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
                    "Access-Control-Allow-Headers":
                      "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
                  },
                });
                await createFabric({
                  variables: {
                    data: {
                      name: fabricNameInput.trim(),
                      shop: localShopId,
                      price: Number(fabricPriceInput || 0),
                      originPrice: Number(fabricOriginPriceInput || 0),
                      pricePerRoll: Number(fabricPricePerRollInput || 0),
                      originPricePerRoll: Number(fabricOriginPricePerRollInput || 0),
                      amountRoll: Number(fabricAmountRollInput || 0),
                      qtyPerRoll: Number(fabricQtyPerRollInput || 0),
                      image: [filename],
                    },
                  },
                });
                await refetchFabrics();
                toast.success(MSG_CREATE_SUCCESS);
                setShowFabricModal(false);
                setFabricNameInput("");
                setFabricPriceInput("0");
                setFabricOriginPriceInput("0");
                setFabricAmountRollInput("0");
                setFabricQtyPerRollInput("0");
                setFabricPricePerRollInput("0");
                setFabricOriginPricePerRollInput("0");
                setFabricImageFile(undefined);
                setFabricImagePreview("");
              } catch (error) {
                toast.error(getApolloErrorMessage(error));
              }
            }}
          >
            {creatingFabric ? <Spinner size="sm" /> : "ບັນທຶກ"}
          </Button>
        </Modal.Footer>
      </Modal>
      <EditFabricModal
        show={showEditFabricModal}
        fabric={editingFabric}
        saving={updatingFabric}
        onHide={() => {
          setShowEditFabricModal(false);
          setEditingFabric(undefined);
        }}
        onSave={onSaveEditFabric}
      />

      <Modal show={showImagePreviewModal} onHide={() => setShowImagePreviewModal(false)} centered size="lg">
        <Modal.Body style={{ textAlign: "center", padding: "10px 0" }}>
          <img src={previewImageUrl} alt="preview" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }} />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ShopSize;
